import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchResource } from '@app/src/api/fetchHooks'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type {
  AssessmentRule,
  AssessmentSection,
  AssessmentTemplate,
  AssessmentTemplateWithClassificationRules,
  Question,
} from '@app/src/types/resourceExplorer'
import { useMemo } from 'react'

const enrichSectionsWithQuestionsAndRules = (
  data: AssessmentTemplateWithClassificationRules | null | undefined,
  rulesBySectionId: Map<number, AssessmentRule[]>,
): AssessmentSection[] => {
  if (!data) return []
  const questionsById = new Map<number, Question>((data.questions ?? []).map(q => [q.id, q]))
  return (data.sections ?? []).map(section => ({
    ...section,
    rules: rulesBySectionId.get(section.id) ?? section.rules ?? [],
    questions: (section.questions ?? []).map(aq => ({
      ...aq,
      question: questionsById.get(aq.questionId) ?? aq.question,
    })),
  }))
}

const useAssessmentTemplateWithQuestions = (
  assessmentTemplateId: number | undefined,
): {
  assessmentTemplate: AssessmentTemplate | undefined
  isLoading: boolean
} => {
  const hasId = assessmentTemplateId != null

  const { items, isLoading: isLoadingSummary } = useFetchCollectionWithPost<AssessmentTemplate>({
    key: FetchKey.AssessmentTemplates,
    endpoint: endpoints.assessmentTemplateCollection,
    payload: {
      filter: [{ name: 'id', filters: [{ value: assessmentTemplateId, operator: Operators.EqualTo }] }],
      include: ['sections.rules'],
      pagination: { itemsPerPage: 1, pageNumber: 1 },
    },
    options: { enabled: hasId },
  })
  const summary = items?.[0]

  const { data, isLoading: isLoadingDetails } = useFetchResource<AssessmentTemplateWithClassificationRules>({
    key: [FetchKey.AssessmentTemplateWithClassificationRules, assessmentTemplateId],
    endpoint: hasId ? endpoints.assessmentTemplateWithClassificationRules(assessmentTemplateId) : '',
    options: { enabled: hasId },
  })

  const rulesBySectionId = useMemo(() => new Map((summary?.sections ?? []).map(s => [s.id, s.rules ?? []])), [summary])

  const sections = useMemo(() => enrichSectionsWithQuestionsAndRules(data, rulesBySectionId), [data, rulesBySectionId])

  return {
    assessmentTemplate: summary ? { ...summary, sections } : undefined,
    isLoading: hasId && (isLoadingSummary || isLoadingDetails),
  }
}

export default useAssessmentTemplateWithQuestions
